<template>
  
  <div>

    <div class="ui large breadcrumb">      
      <a v-if="isCreating" class="clickable" @click="goBack()">
        <i class="reply icon m-r-5"></i> Solicitações
      </a>
      <a v-else class="clickable" @click="$historyBack()">
        <i class="reply icon m-r-5"></i> Voltar
      </a>
      <i class="right chevron icon divider"></i>
      <div class="bold section">
        {{ isCreating ? creatingTitle : updatingTitle }}
      </div>
      <span v-if="isUpdating && !loading" class="animated fadeIn">
        <i class="right chevron icon divider"></i>
        <div class="section">
          <i class="info circle icon"></i> {{ request.id }}
        </div>
        <i class="right chevron icon divider"></i>
        <div class="section">
          <exam-request-status :status="request.status" />
        </div>
      </span>
    </div>

    <bookmark-button v-if="isCreating" :label="creatingTitle" />

    <div class="ui divider"></div>

    <div v-if="loading"><i class="spinner circle loading blue icon"></i> Carregando ...</div>

    <div v-show="!loading" class="animated fadeIn">

      <div class="ui segment">
        <h4 class="ui dividing header">Dados da Solicitação</h4>
        <div class="ui form">          
          <div class="fields">
            <div class="three wide field">
              <label class="form-label">Data <span class="red text">*</span></label>
              <date-picker ref="datePicker" @onChange="onChangeDate" />
            </div>
            <div v-if="$isSimavet()" class="four wide field">
              <label class="form-label">Responsável</label>
              <input :value="request.user.name.toUpperCase() + ' ' + request.user.lastName.toUpperCase()" readonly="readonly">
            </div>      
            <div class="four wide field">
              <label class="form-label">Veterinário <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.vetName" @blur="request.vetName = $toUpperCase($event)" :readonly="$isVet()">
            </div>                                                            
            <div class="five wide field">
              <label class="form-label">Clínica <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.customerName" @blur="request.customerName = $toUpperCase($event)">
            </div>        
          </div>                             
        </div>
      </div>  

      <div class="ui segment">
        <h4 class="ui dividing header">Animal</h4>
        <div class="ui form">          
          <div class="fields">
            <div class="four wide field">
              <label class="form-label">Nome <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.animalName" @blur="request.animalName = $toUpperCase($event)">
            </div>
            <div class="four wide field">
              <label class="form-label">Raça <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.raceName" @blur="request.raceName = $toUpperCase($event)">
            </div>
            <div class="four wide field">
              <label class="form-label">Espécie <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.speciesName" @blur="request.speciesName = $toUpperCase($event)">
            </div>
            <div class="four wide field">
              <label class="form-label">Sexo <span class="red text">*</span></label>
              <select-dropdown ref="animalSexDropdown" @onChange="onChangeAnimalSex" />
            </div>
          </div>   
          <div class="fields">
            <div class="four wide field">
              <label class="form-label">Idade <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.animalAge" @blur="request.animalAge = $toUpperCase($event)">
            </div>
            <div class="four wide field">
              <label class="form-label">Nome do Tutor <span class="red text">*</span></label>
              <input class="uppercase" v-model="request.ownerName" @blur="request.ownerName = $toUpperCase($event)">
            </div>
          </div>                          
        </div>
      </div> 

      <div class="ui segment">
        <h4 class="ui dividing header">Exame</h4>
        <div class="ui form">          
          <div class="fields">            
            <div class="eight wide field">
              <label class="form-label">Tipos de Exames <span class="red text">*</span></label>
              <select-dropdown ref="examTypeDropdown" :multiple="true" @onAdd="onAddExamType" @onRemove="onRemoveExamType" />
            </div>
          </div>     
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Suspeita Clínica <span class="red text">*</span></label>
              <text-editor ref="suspicionTextEditor" />
            </div>
          </div>        
        </div>
      </div> 

      <div class="m-t-20">
        <button class="ui basic button" @click="goBack()">
          <i class="reply icon"></i> Cancelar
        </button>
        <button v-if="isUpdating" class="ui basic red button m-l-10" :class="{ loading: removing }" 
          @click="$refs.confirmRemovalModal.show()">
          <i class="trash icon"></i> Excluir
        </button> 
        <button class="ui right floated blue button" :class="{ loading: saving }" @click="onConfirm()">
          <i class="check icon"></i> {{ isCreating ? 'Confirmar' : 'Atualizar' }}
        </button>         
      </div>

      <yes-no-modal ref="confirmRemovalModal" danger="true"
        :message="`Confirma exclusão da solicitação ${request.id} ?`"  @onYes="onRemove()" />

    </div>  

  </div>

</template> 

<script>

  import moment from "@/utils/moment"
  import ExamRequestService from "@/services/exam-request-service"
  import ExamTypeService from "@/services/exam-type-service"
  import formPage from "@/mixins/form-page"
  import { DatePicker, ExamRequestStatus, SelectDropdown, TextEditor } from "@/components"

  const SEX_OPTIONS = [
    { id: "male", description: '<i class="mars blue icon"></i> Macho' },
    { id: "female", description: '<i class="venus pink icon"></i> Fêmea' },
    { id: "undefined", description: '<i class="question circle grey icon"></i> Indefinido' },
  ]

  export default {
    
    name: "ExamRequestForm",

    mixins: [formPage],

    data() {
      return {
        indexRoute: "/procs/exam-requests",
        creatingTitle: "Nova Solicitação de Exame",
        updatingTitle: "Alterar Solicitação de Exame",
        service: new ExamRequestService(),
        examTypeService: new ExamTypeService(),
        request: { 
          status: "waiting_certs",
          date: new Date(),
          user: this.$store.state.auth.user,
          userId: this.$store.state.auth.user.id,  
          examTypes: [],
        },
      }
    }, 
    
    components: {
      DatePicker,
      ExamRequestStatus,
      SelectDropdown,
      TextEditor,
    }, 

    created() {

      if (this.$isVet() && !this.$store.state.auth.user.vet) {
        this.allowed = false
        this.$msg.warn("Seu usuário não está associado a um veterinário. Por favor acione a Simavet.", 8000)
        this.$router.push("/procs/exam-requests")        
      }
    },

    mounted() {      
      this.loadRequest()      
    },

    methods: {

      async loadRequest() {

        if (!this.allowed)
          return

        this.loading = true        

        try {

          if (this.isCreating && this.$isVet() && this.$store.state.auth.user.vet)
            this.request.vetName = this.$store.state.auth.user.vet.name

          if (this.isUpdating)
            this.request = await this.service.findById(this.$route.params.id)

          if (this.request.id && !this.service.canUpdate(this.request)) {
            this.$router.push(`/procs/exam-requests/${this.request.id}/show`) 
            return
          }  
          
          this.$refs.datePicker.setDate(this.request.date)
          this.$refs.animalSexDropdown.setValues(SEX_OPTIONS, this.request.animalSex)          

          if (this.request.suspicionContents)
            this.$refs.suspicionTextEditor.setContents(this.request.suspicionContents)

          await this.loadExamTypes()  

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.loading = false
        }
      },

      async loadExamTypes() {

        this.loading = true

        try {

          const types = await this.examTypeService.findAll({
            attributes: ["id", "description"],
            order: ["description"]
          })

          let examTypeIds = []
        
          this.request.examTypes.forEach((type) => examTypeIds.push(type.examTypeId))

          this.$refs.examTypeDropdown.setValues(types, examTypeIds)

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.loading = false
        }
      },

      onChangeDate(date) {
        this.request.date = date
        this.refreshData()
      },

      onChangeAnimalSex(id) {
        this.request.animalSex = id
        this.refreshData()
      },

      onAddExamType(id) {

        id = parseInt(id)

        const ids = this.request.examTypes.map((type) => type.examTypeId)

        if (ids.indexOf(id) == -1) {
          this.request.examTypes.push({ examTypeId: id })
          this.refreshData()
        }  
      },

      onRemoveExamType(id) {
        
        let index = 0

        for (let type of this.request.examTypes) {          
          if (type.examTypeId == id)
            break
          index++  
        }

        this.request.examTypes.splice(index, 1)
        this.refreshData()
      },

      refreshData() {
        this.request = this.$cloneObject(this.request)
      },

      async onConfirm() {

        if (!this.validData())
          return

        this.saving = true

        try {

          this.request.date = moment(this.request.date).format("YYYY-MM-DD")
          this.request.suspicion = this.$refs.suspicionTextEditor.getText()
          this.request.suspicionContents = this.$refs.suspicionTextEditor.getContents({ toString: true })

          if (this.isCreating) {
            await this.service.create(this.request)
          } else {
            await this.service.update(this.request)
          }

          this.$reloadDataTables(["exam-requests"])

          this.$msg.success(this.isCreating ? "Solicitação criada com sucesso" : "Solicitação atualizada com sucesso")

          this.goBack()

        } catch (error) {

          this.saving = false

          this.$errorHandler(error)
        }
      },

      validData() {

        let isOk = true
        let message = ""
        const { date, vetName, customerName, animalName, raceName, speciesName, 
                animalSex, animalAge, ownerName, examTypes } = this.request

        if (!date) {
          message = "Informe a data da solicitação"
        } else if (!vetName) {
          message = "Informe o nome do veterinário"
        } else if (!customerName) {
          message = "Informe o nome da clínica"
        } else if (!animalName) {
          message = "Informe o nome do animal"
        } else if (!raceName) {
          message = "Informe a raça do animal"      
        } else if (!speciesName) {
          message = "Informe a espécie do animal"        
        } else if (!animalSex) {
          message = "Informe o sexo do animal"          
        } else if (!animalAge) {
          message = "Informe a idade do animal"            
        } else if (!ownerName) {
          message = "Informe o nome do tutor do animal"              
         } else if (examTypes.length == 0) {
          message = "Informe os tipos de exames"
        } else if (this.$refs.suspicionTextEditor.isEmpty()) {
          message = "Informe a suspeita clínica"                 
        }

        if (message) {
          isOk = false
          this.$msg.info(message)
        }

        return isOk
      },

      async onRemove() {
        
        this.removing = true

        try {

          await this.service.remove(this.request)

          this.$reloadDataTables(["exam-requests"])

          this.$msg.success("Solicitação excluída com sucesso")

          this.$router.push("/procs/exam-requests")

        } catch (error) {

          this.removing = false

          this.$errorHandler(error)
        }
      },
    },
  }

</script>